import React, { useMemo, useState, useEffect } from "react";
import { useMemoizedFn } from "ahooks";
import { Button, Form, Input, Select, Space, Card, Divider } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { StakingFormProps } from "../../models/type";
import style from "./index.module.less";
import { fetchSubnetList } from "@/api";
import type { SubnetData } from "@/api";
import { useWatch } from "antd/es/form/Form";
import { useAccount } from "wagmi";

type listItem = { label: string; value: number };

interface BatchStakeData {
  netuid: number;
  amount: string;
  slippage: string;
}

interface BatchAddStakeFormProps extends Omit<StakingFormProps, "onSubmit"> {
  onSubmit: (values: BatchStakeData[]) => void;
}

export const BatchAddStakeForm = ({
  onSubmit,
  loading = false,
  maxBalance,
}: BatchAddStakeFormProps) => {
  const { address: account } = useAccount();
  const [form] = Form.useForm();
  const [data, setData] = useState<SubnetData[]>([]);
  const [subnet_data, setSubnetData] = useState<listItem[]>([]);
  const [formItems, setFormItems] = useState<number[]>([0]); // Track form items

  useEffect(() => {
    fetchSubnetList("1h")
      .then((res) => {
        const res1 = res.filter((item) => item.netid > 0);
        setData(res1);
        const list = res1.map((d) => {
          return { label: `${d.netid} | ${d.subnet_name}`, value: d.netid };
        });
        setSubnetData(list);
      })
      .catch((error) => {
        console.error("Error fetching user info:", error);
      });
  }, []);

  const addForm = useMemoizedFn(() => {
    const newId = Math.max(...formItems) + 1;
    setFormItems([...formItems, newId]);
  });

  const removeForm = useMemoizedFn((id: number) => {
    if (formItems.length > 1) {
      setFormItems(formItems.filter((item) => item !== id));
      // Clear form fields for removed item
      form.setFieldValue(`netuid_${id}`, undefined);
      form.setFieldValue(`amount_${id}`, undefined);
      form.setFieldValue(`slippage_${id}`, undefined);
    }
  });

  const handleMax = useMemoizedFn((id: number) => {
    const availableBalance = maxBalance || 0;
    const usedBalance = formItems
      .filter((itemId) => itemId !== id)
      .reduce((total, itemId) => {
        const amount = form.getFieldValue(`amount_${itemId}`);
        return total + (parseFloat(amount) || 0);
      }, 0);
    const maxForThis = Math.max(0, availableBalance - usedBalance);
    form.setFieldValue(`amount_${id}`, maxForThis);
  });

  const handleSubmit = useMemoizedFn((values: any) => {
    const batchData: BatchStakeData[] = formItems
      .map((id) => ({
        netuid: values[`netuid_${id}`],
        amount: values[`amount_${id}`],
        slippage: values[`slippage_${id}`] || "1",
      }))
      .filter(
        (item) => item.netuid && item.amount && parseFloat(item.amount) > 0
      );

    onSubmit(batchData);
  });

  const getNetuidInfo = useMemoizedFn((netuid: number) => {
    return data.find((d) => d.netid === netuid);
  });

  const totalAmount = useMemo(() => {
    return formItems.reduce((total, id) => {
      const amount = form.getFieldValue(`amount_${id}`);
      return total + (parseFloat(amount) || 0);
    }, 0);
  }, [formItems, form]);

  return (
    <Form
      form={form}
      onFinish={handleSubmit}
      layout="vertical"
      className={style.batch_stake_form}
    >
      <div className={style.summary}>
        <div>Total Amount: {totalAmount.toFixed(9)}t</div>
        <div>Available Balance: {maxBalance}t</div>
        <div>Remaining: {((maxBalance || 0) - totalAmount).toFixed(9)}t</div>
      </div>

      <div className={style.forms_grid}>
        {formItems.map((id, index) => (
          <Card
            key={id}
            className={style.form_card}
            title={`Stake #${index + 1}`}
            extra={
              formItems.length > 1 && (
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => removeForm(id)}
                  danger
                >
                  Remove
                </Button>
              )
            }
          >
            <Form.Item
              label="Netuid"
              name={`netuid_${id}`}
              rules={[
                {
                  required: true,
                  message: "Please select netuid!",
                },
              ]}
            >
              <Select
                size="large"
                placeholder="Select Netuid"
                options={subnet_data}
              />
            </Form.Item>

            <Form.Item
              label="Amount"
              name={`amount_${id}`}
              rules={[
                { required: true, message: "Please input stake amount!" },
                {
                  validator: (_, value) => {
                    const regex = /^\d*\.?\d{0,9}$/;
                    const reg = regex.test(value);

                    if (!reg) {
                      return Promise.reject(
                        new Error("Please enter the correct amount")
                      );
                    }

                    if (totalAmount > (maxBalance || 0)) {
                      return Promise.reject(
                        new Error("Total amount exceeds available balance")
                      );
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                size="large"
                autoComplete="off"
                suffix={
                  <div className={style.max} onClick={() => handleMax(id)}>
                    MAX
                  </div>
                }
              />
            </Form.Item>

            <Form.Item
              label="Slippage (%)"
              name={`slippage_${id}`}
              initialValue="1"
              rules={[{ required: true, message: "Please input slippage!" }]}
            >
              <Input
                size="large"
                autoComplete="off"
                suffix={<div className={style.rate}>%</div>}
              />
            </Form.Item>

            <StakeInfo
              netuidFieldName={`netuid_${id}`}
              amountFieldName={`amount_${id}`}
              slippageFieldName={`slippage_${id}`}
              form={form}
              data={data}
            />
          </Card>
        ))}
      </div>

      <div className={style.form_actions}>
        <Button
          type="dashed"
          onClick={addForm}
          icon={<PlusOutlined />}
          block
          size="large"
        >
          Add Another Stake
        </Button>

        <Divider />

        <Button
          type="primary"
          htmlType="submit"
          className={style.submit_button}
          loading={loading}
          size="large"
          block
        >
          Batch Add Stakes
        </Button>
      </div>
    </Form>
  );
};

// Helper component to show stake info for each form
const StakeInfo = ({
  netuidFieldName,
  amountFieldName,
  slippageFieldName,
  form,
  data,
}: {
  netuidFieldName: string;
  amountFieldName: string;
  slippageFieldName: string;
  form: any;
  data: SubnetData[];
}) => {
  const netuid = useWatch(netuidFieldName, form);
  const amount = useWatch(amountFieldName, form);
  const slippage = useWatch(slippageFieldName, form);

  const netidInfo = useMemo(() => {
    return data.find((d) => d.netid === Number(netuid));
  }, [netuid, data]);

  const receivedAmount = useMemo(() => {
    if (!netidInfo?.price || !amount) {
      return 0;
    }
    return amount / ((Number(slippage) * 0.01 + 1) * netidInfo?.price);
  }, [amount, netidInfo?.price, slippage]);

  if (!netidInfo) return null;

  return (
    <div className={style.stake_info}>
      <div className={style.info_item}>
        <span>Price:</span>
        <span>
          1 {netidInfo.subnet_symbol} = {netidInfo.price}T
        </span>
      </div>
      <div className={style.info_item}>
        <span>You will receive:</span>
        <span>
          {receivedAmount.toFixed(9)} {netidInfo.subnet_symbol}
        </span>
      </div>
    </div>
  );
};
